import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus, faTasks, faCheck, faTimes, faFileExcel,faEye,faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import FormModal from './FormModal';
import ViewModal from './ViewModal';
import '../styles/DataList.css';
import { useAuth } from '../hooks/useAuth';
import { CSVLink } from "react-csv";
import { getFormattedDateTime,getFormattedDate } from '../assets/Utils';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { openDB } from 'idb';
import * as XLSX from 'xlsx'; 

const DataList = ({ apiConfig, columns }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const { rolePermissions, user, token } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(false);
  const [resetToggle, setResetToggle] = useState(false);

  const navigate = useNavigate();  

  useEffect(() => {
    fetchData();
  }, [isOnline]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);


  useEffect(() => {
    applyFilters();
  }, [filters, data]);


  useEffect(() => {
    const init = {};
    console.log("Initializing filters with columns:", columns);
    const today = new Date();
    const todayDate = today.toISOString().slice(0, 10);
    const todayDateTimeStart = new Date(today.setHours(0, 0, 0, 0)).toISOString();
    const todayDateTimeEnd = new Date(today.setHours(23, 59, 59, 999)).toISOString();

    columns.forEach(col => {
      if (!col.filterable || col.filterDefault == null) return;

      if (col.type === 'date') {
        // defaultValue is either "Today" or "YYYY-MM-DD - YYYY-MM-DD"
        if (col.filterDefault === 'Today') {
          init[col.name] = { start: todayDate, end: todayDate };
        } else if (col.filterDefault === 'BLANK') {
          init[col.name] = { start: '', end: '' };
        } else if (col.filterDefault.includes('-')) {
          const [from, to] = col.filterDefault.split('-').map(s => s.trim());
          init[col.name] = { start: from, end: to };
        }
      } else if (col.type === 'datetime') {
        // defaultValue is either "Today" or "YYYY-MM-DDTHH:mm:ssZ - YYYY-MM-DDTHH:mm:ssZ"
        if (col.filterDefault === 'Today') {
          init[col.name] = { start: todayDateTimeStart, end: todayDateTimeEnd };
        } else if (col.filterDefault === 'BLANK') {
          console.log("Setting BLANK filter for column:", col.name);
          init[col.name] = { start: '', end: '' };
        } else if (col.filterDefault.includes('-')) {
          const [from, to] = col.filterDefault.split('-').map(s => s.trim());
          init[col.name] = { start: from, end: to };
        }
      } else if (col.type === 'boolean') {
        init[col.name] = Boolean(col.filterDefault);
      } else {
        init[col.name] = String(col.filterDefault);
      }
    });

    setFilters(init);
  }, [columns]);


  const fetchData = async () => {
    setIsLoading(true);
  
    try {
      const useLocalCaching = apiConfig.useLocalCaching;
      const entityName = apiConfig.title.replace(/\s+/g, ''); // Strip whitespace from the title
  
      if (useLocalCaching) {

        let dbVersion = parseInt(localStorage.getItem('dbVersion'), 10);
        if (!dbVersion) {
          dbVersion = 2;
          localStorage.setItem('dbVersion', dbVersion);
        }

        const keyColumn = columns.find((col) => col.identifier === true);
        const keyPath = keyColumn ? keyColumn.id : 'id'; // Fallback to 'id' if none is marked

        console.log("appDB version number: ",dbVersion);

        let db = await openDB('appDB', dbVersion, {
          upgrade(db) {
            console.log("loading db with upgrade");
            if (!db.objectStoreNames.contains(entityName)) {
              db.createObjectStore(entityName, { keyPath }); // Ensure object store exists
            }
          },
        });

        if (!db.objectStoreNames.contains(entityName)) {
          console.log("object number found incrementing dbVersion");
          const nextDbVersion = db.version + 1;
          db.close();
          db = await openDB('appDB', nextDbVersion, {
            upgrade(db) {
              if (!db.objectStoreNames.contains(entityName)) {
                console.log("Creating object ",entityName," with version:",nextDbVersion);
                db.createObjectStore(entityName, { keyPath });
              }
            },
          });
          localStorage.setItem('dbVersion', nextDbVersion);
        }
  
        if(isOnline) {
            const cachedCount = await db.count(entityName);
            const storedLastSync =
              localStorage.getItem(`lastSync_${entityName}`) ||
              '1970-01-01T00:00:00Z';
            // Empty IndexedDB but a recent lastSync means we need a full resync
            const lastSync =
              cachedCount === 0 ? '1970-01-01T00:00:00Z' : storedLastSync;
            console.log("Getting data with last sync date of: ", lastSync);

            const response = await axios.get(
              `${process.env.REACT_APP_SERVER_BASE_URL}${apiConfig.fetchEndpoint}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { lastSync },
              },
            );

            const updatedRecords = Array.isArray(response.data)
              ? response.data
              : [];

            const tx = db.transaction(entityName, 'readwrite');
            const store = tx.objectStore(entityName);

            for (const record of updatedRecords) {
              store.put(record);
            }
            await tx.done;

            // Advance lastSync only from server timestamps, never blindly to "now"
            if (updatedRecords.length > 0) {
              const maxUpdatedMs = updatedRecords.reduce((max, record) => {
                const ts = record.updatedAt
                  ? new Date(record.updatedAt).getTime()
                  : 0;
                return ts > max ? ts : max;
              }, new Date(lastSync).getTime());
              localStorage.setItem(
                `lastSync_${entityName}`,
                new Date(maxUpdatedMs).toISOString(),
              );
            }
        }
        // Retrieve all data from IndexedDB and update React state
        const allData = await db.getAll(entityName);
        setData(allData);
      } else {
        // Fallback: Fetch all data from the server
        const response = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}${apiConfig.fetchEndpoint}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getValueBySelector = (item, selector) => {
    if (!selector) return null;
    const keys = selector.split('.');
    return keys.reduce((value, key) => (value ? value[key] : ''), item);
  };


  const applyFilters = () => {
    const processed = {};

    // 1) user-entered filters
    const helperNames = new Set((apiConfig.helperFilters || []).map((helper) => helper.name));
    Object.keys(filters).forEach((key) => {
      if (helperNames.has(key)) return;
      const col = columns.find((c) => c.name === key);
      const val = filters[key];
      if (col?.type === 'integer') {
        processed[key] = val ? parseInt(val, 10) : '';
      } else if (col?.type === 'date' || col?.type === 'datetime') {
        processed[key] = val;
      } else if (col?.type === 'boolean') {
        // For boolean filters, we need to check if the filter is active
        // If the checkbox is checked, filter for the filterDefault value
        // If unchecked, don't apply any filter (null)
        if (val === true) {
          processed[key] = col.filterDefault;
        } else {
          // Check if this filter has a filterDefault and was initially set
          if (col.filterDefault != null && filters.hasOwnProperty(key)) {
            // Filter exists but is unchecked, so don't filter
            processed[key] = null;
          } else {
            // No filter set at all
            processed[key] = null;
          }
        }
      } else {
        processed[key] = String(val).toLowerCase();
      }
    });

    // 2) systemFilters override/augment
    if (apiConfig.systemFilters) {
      apiConfig.systemFilters.forEach(({ name, value }) => {
        const col = columns.find((c) => c.name === name);
        if (col?.type === 'integer') {
          processed[name] = parseInt(value, 10);
        } else if (col?.type === 'date' || col?.type === 'datetime') {
          // if ever needed: parsed similarly
          processed[name] = value;
        } else if (col?.type === 'boolean') {
          processed[name] = Boolean(value);
        } else {
          processed[name] = String(value).toLowerCase();
        }
      });
    }

    // 3) apply to data
    const helperFilters = apiConfig.helperFilters || [];
    const filtered = data.filter((item) => {
      const matchesColumns = Object.keys(processed).every((key) => {
        const filterVal = processed[key];
        const col = columns.find((c) => c.name === key);
        const sel = col?.filterSelector || key;
        const itemVal = getValueBySelector(item, sel);

        if (col?.type === 'integer') {
          return !filterVal || itemVal === filterVal;
        }

        if (col?.type === 'boolean') {
          // If filter is null (unchecked or no filter), show all records
          if (filterVal === null) {
            return true;
          }
          
          // Convert both values to boolean for comparison
          // Handle various data types: 1/0, "1"/"0", true/false, "true"/"false"
          const normalizeBoolean = (val) => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'boolean') return val;
            if (typeof val === 'number') return val === 1;
            if (typeof val === 'string') {
              const str = val.toLowerCase();
              return str === 'true' || str === '1';
            }
            return Boolean(val);
          };
          
          const normalizedItemVal = normalizeBoolean(itemVal);
          const normalizedFilterVal = normalizeBoolean(filterVal);
          
          
          return normalizedItemVal === normalizedFilterVal;
        }

        if (col?.type === 'date' || col?.type === 'datetime') {
          const { start, end } = filterVal || {};
          console.log("Applying date filter for column:", key, "with start:", start, "and end:", end);
          // Special handling for BLANK filter
          if (start === '' && end === '') {
            console.log("Date filter is BLANK and value is:", itemVal);
            return itemVal === null || itemVal === undefined || itemVal === '';
          }
          
          // Normal date range filtering
          const d = itemVal ? String(itemVal).slice(0, 10) : '';
          if (start && d < start) return false;
          if (end && d > end) return false;
          return true;
        }

        // default text/includes match
        return (
          !filterVal ||
          String(itemVal || '').toLowerCase().includes(filterVal)
        );
      });

      if (!matchesColumns) return false;

      return helperFilters.every((helper) => {
        if (!filters[helper.name] || typeof helper.match !== 'function') return true;
        return Boolean(helper.match(item));
      });
    });

    setFilteredData(filtered);
  };


  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox for boolean filters
    if (type === 'checkbox') {
      setFilters(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // date‐range fields are named "ColName_start" or "ColName_end"
    if (name.endsWith('_start') || name.endsWith('_end')) {
      const [colName, bound] = name.split('_'); // e.g. ["Date", "start"]
      setFilters(prev => ({
        ...prev,
        [colName]: {
          ...((prev[colName] && typeof prev[colName]==='object')? prev[colName] : {}),
          [bound]: value
        }
      }));
    } else {
      // simple text/integer filter
      setFilters(prev => ({ ...prev, [name]: value }));
    }
    console.log("Filters updated:", filters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const renderFilters = () => {
    const dateFilters = columns.filter(c => c.filterable && (c.type === 'date' || c.type === 'datetime'));
    const booleanFilters = columns.filter(c => c.filterable && c.type === 'boolean');
    const regularFilters = columns.filter(c => c.filterable && c.type !== 'date' && c.type !== 'datetime' && c.type !== 'boolean');
    const helperFilters = apiConfig.helperFilters || [];

    return (
      <div className="filter-container">
        <div className="regular-filters">
          {regularFilters.map(col => (
            <div key={col.name} className="filter-item">
              <input
                type="text"
                name={col.name}
                placeholder={`Filter by ${col.name}`}
                value={filters[col.name] || ''}
                onChange={handleFilterChange}
              />
            </div>
          ))}
        </div>

        <div className="boolean-filters">
          {booleanFilters.map(col => (
            <div key={col.name} className="boolean-filter">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name={col.name}
                  checked={filters[col.name] || false}
                  onChange={handleFilterChange}
                />
                {col.name}
              </label>
            </div>
          ))}
        </div>

        {helperFilters.length > 0 && (
          <div className="helper-filters">
            <span className="helper-filters-label">{apiConfig.helperFiltersLabel || 'Find'}</span>
            {helperFilters.map((helper) => (
              <div key={helper.name} className="boolean-filter">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name={helper.name}
                    checked={Boolean(filters[helper.name])}
                    onChange={handleFilterChange}
                  />
                  {helper.label || helper.name}
                </label>
              </div>
            ))}
          </div>
        )}

        <div className="date-filters">
          {dateFilters.map(col => (
            <div key={col.name} className="date-filter">
              <div className="date-range-container">
                <label>{col.name}</label>
                {(filters[col.name]?.start === '' && filters[col.name]?.end === '') ? (
                  <div className="blank-filter">Empty/Blank Values Only</div>
                ) : (
                  <div className="date-range-inputs">
                    <input
                      type="date"
                      name={`${col.name}_start`}
                      value={col.type === 'datetime' ? (filters[col.name]?.start?.slice(0, 10) || '') : (filters[col.name]?.start || '')}
                      onChange={handleFilterChange}
                      placeholder="From"
                    />
                    <span>–</span>
                    <input
                      type="date"
                      name={`${col.name}_end`}
                      value={col.type === 'datetime' ? (filters[col.name]?.end?.slice(0, 10) || '') : (filters[col.name]?.end || '')}
                      onChange={handleFilterChange}
                      placeholder="To"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="filter-actions">
          <button className="clear-filters" onClick={clearFilters}>
            Clear
          </button>
          {apiConfig.allowExport && (
            <button className="export-button" onClick={handleExcelExport}>
              Export <FontAwesomeIcon icon={faFileExcel} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleExcelExport = async () => {
    try {
      // Use columns to map and extract values from filteredData
      const processedData = filteredData.map((row) => {
        const processedRow = {};
  
        columns.forEach((col) => {
          // Use the selector function if it exists, otherwise use the property name
          if (col.selector) {
            processedRow[col.name] = col.selector(row);
          } else if (col.id) {
            processedRow[col.name] = row[col.id];
          } else {
            processedRow[col.name] = row[col.name];
          }
        });
  
        return processedRow;
      });
  
      // Convert the processed data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(processedData);
  
      // Create a new workbook
      const workbook = XLSX.utils.book_new();
  
      // Append the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
      // Export the workbook as an Excel file
      XLSX.writeFile(workbook, `${apiConfig.title.replace(/\s+/g, '_').toLowerCase()}_export.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };
  
  const handleDelete = async (row) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        const id = getIdentifierValue(row);

        await axios.delete(`${process.env.REACT_APP_SERVER_BASE_URL}${apiConfig.deleteEndpoint}/${id}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },

            }
        );

      // If local caching is enabled, remove the record from local IndexedDB.
      if (apiConfig.useLocalCaching) {
        const entityName = apiConfig.title.replace(/\s+/g, '');
        const dbVersion = parseInt(localStorage.getItem('dbVersion'), 10) || 2;
        const db = await openDB('appDB', dbVersion, {
          upgrade(db) {
            if (!db.objectStoreNames.contains(entityName)) {
              db.createObjectStore(entityName, { keyPath: 'id' });
            }
          },
        });
        await db.delete(entityName, id);
      }

        fetchData();
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('Failed to delete record.');
      }
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    
    // Call data enhancement hook if provided (for extending dropdown options, etc.)
    if(apiConfig.onEditEnhance){
      apiConfig.onEditEnhance(record);
    }
    
    // Check if custom edit handler is provided (completely overrides default behavior)
    if(apiConfig.onEdit){
      apiConfig.onEdit(record);
    } else {  
      setIsViewModalVisible(false);
      setIsModalVisible(true);
    }
  };

  const handleAdd = async () => {
    if(isButtonsDisabled) return;
    if (apiConfig.onAdd) {
      apiConfig.onAdd();
      return;
    }
    let prefill = null;
    if (apiConfig.onBeforeAdd) {
      prefill = await apiConfig.onBeforeAdd();
      if (prefill === false) return; // consumer blocked the add
    }
    setSelectedRecord(prefill || null);
    setIsViewModalVisible(false);
    setIsModalVisible(true);
    setIsButtonsDisabled(true);
  }

  const handleView = (record) => {
    if (apiConfig.onView) {
      apiConfig.onView(record);
      return;
    }
    setSelectedRecord(record);
    setIsViewModalVisible(true);
  };

  const buildClonedRecord = (sourceRecord) => {
    const cloned = JSON.parse(JSON.stringify(sourceRecord || {}));
    const fields = apiConfig.fields || [];

    fields
      .filter((field) => field.identifier)
      .forEach((field) => {
        delete cloned[field.name];
      });

    ['createdAt', 'updatedAt', 'updatedBy', 'createdBy', 'deletedAt'].forEach((key) => {
      delete cloned[key];
    });

    fields
      .filter((field) => field.type === 'file')
      .forEach((field) => {
        cloned[field.name] = null;
      });

    (apiConfig.cloneOmitFields || []).forEach((fieldName) => {
      delete cloned[fieldName];
    });

    if (typeof apiConfig.cloneTransform === 'function') {
      const transformed = apiConfig.cloneTransform(cloned, sourceRecord);
      if (transformed && typeof transformed === 'object') {
        return transformed;
      }
    }

    return cloned;
  };
  

  const handleBulkAction = async (action) => {
    if (!action) return;

    if (selectedRows.length === 0) {
      alert('Please select at least one record for bulk action.');
      return;
    }

    const canAddPermission = rolePermissions?.[apiConfig.path]?.Add === 'true' && apiConfig.allowAdd;

    if (action.type === 'clone') {
      if (!canAddPermission) {
        alert('You do not have permission to clone records on this list.');
        return;
      }

      if (selectedRows.length !== 1) {
        alert('Please select exactly one record to clone.');
        return;
      }

      const sourceRecord = selectedRows[0];
      const clonedRecord = buildClonedRecord(sourceRecord);

      setSelectedRecord(clonedRecord);
      setIsViewModalVisible(false);
      setIsModalVisible(true);
      setIsButtonsDisabled(true);
      setSelectedRows([]);
      setResetToggle((prev) => !prev);
      return;
    }

    if(isButtonsDisabled) return;
    setIsButtonsDisabled(true);
    if (window.confirm(`Are you sure you want to execute '${action.name}' for the selected records?`)) {
      try {
        const selectedIds = selectedRows
          .map((row) => getIdentifierValue(row))
          .filter((id) => id !== null && id !== undefined && id !== '');

        if (selectedIds.length === 0) {
          alert('Could not resolve selected record identifiers.');
          return;
        }

        const payload = {
          ids: selectedIds,
          updatedBy: user.portal_id,
          ...(action.payload || {}),
        };

        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}${action.endpoint}`,
          payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: action.download ? 'blob' : 'json',
            }
        );

        if (action.download) {
          const contentDisposition = response.headers['content-disposition'] || '';
          const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
          const filename = filenameMatch?.[1] || action.downloadFileName || 'download.xlsx';

          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.setAttribute('download', filename);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          downloadLink.remove();
          window.URL.revokeObjectURL(url);
          alert(`Bulk action '${action.name}' downloaded successfully!`);
        } else {
          alert(`Bulk action '${action.name}' executed successfully!`);
        }
      } catch (error) {
        console.error(`Error executing bulk action '${action.name}':`, error);
        alert(`Failed to execute '${action.name}': ${error.response?.data?.error || error.message}`);
      } finally {
        setSelectedRows([]);
        setResetToggle(prev => !prev);
        fetchData();
        // check if onUpdateSuccess function is provided
        if (apiConfig.onUpdateSuccess) {
          apiConfig.onUpdateSuccess();
        }
      }
    }
    setIsButtonsDisabled(false);
  };

  const handleSelectedRowsChange = (state) => {
    setSelectedRows(state.selectedRows || []);
  };

  const renderColumnCell = (column, row) => {
    if (column.visible && typeof column.visible === 'function' && !column.visible(row)) {
      return null;
    }
    // Handle boolean type with icons
    if (apiConfig.conditionalStyles?.[column.name]) {
        const conditions = apiConfig.conditionalStyles[column.name];
        const style = calculateConditionalStyle(column.selector(row), conditions);
        return <span style={{...style,"padding":"5px"}}>{column.selector(row)}</span>;
      }
    
      if (column.type === 'boolean') {
        return column.selector(row) ? (
          <FontAwesomeIcon icon={faCheck} className="boolean-true" />
        ) : (
          <FontAwesomeIcon icon={faTimes} className="boolean-false" />
        );
      }
      if(column.type === 'datetime') {
        return column.selector(row) ? (getFormattedDateTime(column.selector(row))):('');
      }
      
      if(column.type === 'date') {
        return column.selector(row) ? (getFormattedDate(column.selector(row))):('');
      }
    
      if(column.type === 'link') {
        const label =
        typeof column.label === 'function' ? column.label(row) : column.label;
      return (
        <Link
          to={column.path}
          state={typeof column.getState === 'function' ? column.getState(row) : column.getState}
        >
          {label}
        </Link>
      );
      }
      return column.selector(row);
  };
  
  const getIdentifierValue = (row) => {
    const identifierCol = columns.find((col) => col.identifier);
    return identifierCol ? identifierCol.selector(row) : null;
  };  
  
  const calculateConditionalStyle = (value, style) => {
    if (style.conditions.min !== undefined || style.conditions.max !== undefined) {
      const { min, max, colors } = style.conditions;
      if ((min !== undefined && value < min) || (max !== undefined && value > max)) {
        return colors.outOfRange ;
      }
      return colors.withinRange;
    }
  
    if (style.type === 'text' && style.conditions.matches) {
      const color = style.conditions.matches[value] || style.conditions.default;
      return color;
    }
  
    return {};
  };

  const isExpandableRowsEnabled = apiConfig.expandableRows !== 'Off';
  const isRowsExpandedByDefault = apiConfig.expandableRows === 'Always';
  const canAddPermission = rolePermissions?.[apiConfig.path]?.Add === 'true' && apiConfig.allowAdd;
  const canEditPermission = rolePermissions?.[apiConfig.path]?.Edit === 'true';
  const allowedBulkActions = (apiConfig.bulkActions || []).filter((action) => {
    if (action.type === 'clone') {
      return canAddPermission;
    }
    return canEditPermission;
  });
  const allowedHeaderActions = (apiConfig.headerActions || []).filter((action) => {
    const permission = action.permission || 'Add';
    return rolePermissions?.[apiConfig.path]?.[permission] === 'true';
  });

  return (
    rolePermissions?.[apiConfig.path]?.View !== 'true' ? (
        <div className="noAccess">
            <FontAwesomeIcon icon={faExclamationTriangle} className="noAccess-icon" />
            <p>You do not have permission to access this page.</p>
        </div>
    ) : (
    
    <div className="data-list">


      <div className="header">
        <h2>{apiConfig.title}</h2>
        {!isOnline && <p className="offline-warning">You are OFFLINE. Limitted functionality until intternet is restored.</p>}
        <div className="actions">
          {allowedHeaderActions.map((action) => (
            <button
              key={action.name}
              type="button"
              disabled={!isOnline || isButtonsDisabled || action.disabled}
              className="header-action-button"
              onClick={() => {
                if (typeof action.onClick === 'function') {
                  action.onClick();
                }
              }}
            >
              {action.icon ? <FontAwesomeIcon icon={action.icon} /> : null}
              <span>{action.name}</span>
            </button>
          ))}
          {rolePermissions?.[apiConfig.path]?.Add === 'true' && apiConfig.allowAdd && (
            <button
              disabled={!isOnline || isButtonsDisabled}
              className={`add-button${apiConfig.addButtonLabel ? ' add-button-labeled' : ''}`}
              onClick={() => handleAdd()}
            >
              <FontAwesomeIcon icon={faPlus} />
              {apiConfig.addButtonLabel ? <span>{apiConfig.addButtonLabel}</span> : null}
            </button>
          )}
          {selectedRows.length > 0 && allowedBulkActions.length > 0 && (
            <div className="bulk-actions">
              <FontAwesomeIcon icon={faTasks} />
              <select
                disabled={!isOnline}
                defaultValue=""
                onChange={(e) =>
                  handleBulkAction(allowedBulkActions.find((action) => action.name === e.target.value))
                }
              >
                <option value="">Select Action</option>
                {allowedBulkActions.length > 0
                  ? allowedBulkActions.map((action) => (
                      <option key={action.name} value={action.name}>
                        {action.name}
                      </option>
                    ))
                  : <option value="" disabled>No actions available</option>}
              </select>
            </div>
          )}
        </div>
      </div>

      <DataTable
        columns={[
          ...columns.map((col) => ({
            ...col,
            cell: (row) => (typeof col.cell === 'function' ? col.cell(row) : renderColumnCell(col, row)),
          })),
          {
            id: 'row-actions',
            name: 'Actions',
            button: true,
            grow: 0,
            width: '108px',
            ignoreRowClick: true,
            allowOverflow: true,
            cell: (row) => (
              <div className="data-list-row-actions">
                <button type="button" title="View" aria-label="View" onClick={() => handleView(row)}>
                    <FontAwesomeIcon icon={faEye} />
                </button>
                {rolePermissions?.[apiConfig.path]?.Edit === 'true' && apiConfig.allowEdit && (
                  <button type="button" title="Edit" aria-label="Edit" disabled={!isOnline} onClick={() => handleEdit(row)}>
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
                {rolePermissions?.[apiConfig.path]?.Delete === 'true' && apiConfig.allowEdit && apiConfig.allowDelete !== false && (
                  <button type="button" title="Delete" aria-label="Delete" disabled={!isOnline} onClick={() => handleDelete(row)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            ),
          },
        ]}
        data={filteredData}
        progressPending={isLoading}
        selectableRows
        onSelectedRowsChange={handleSelectedRowsChange}
        clearSelectedRows={resetToggle}
        pagination
        paginationPerPage={apiConfig.pageSize || 50}
        paginationRowsPerPageOptions={apiConfig.paginationRowsPerPageOptions || [10, 25, 50, 100]}
        highlightOnHover
        defaultSortFieldId={apiConfig.defaultSortFieldId} // New default sort field
        defaultSortAsc={apiConfig.defaultSortAsc ?? true} // New sort order
        subHeader
        subHeaderComponent={renderFilters()}
        expandableRows={isExpandableRowsEnabled}
        expandableRowExpanded={() => isRowsExpandedByDefault}
        expandableRowsComponent={({ data }) =>
          apiConfig.expandableRowsComponent ? (
            React.createElement(apiConfig.expandableRowsComponent, { row: data })
          ) : null
        }
        conditionalRowStyles={apiConfig.conditionalRowStyles || []}
       // onRowClicked={handleView} 
      />

      {isModalVisible && (
        <FormModal
          initialData={selectedRecord}
          onClose={() => {
            setIsModalVisible(false);
            setIsButtonsDisabled(false);
            setSelectedRecord(null);
            fetchData();
          }}
          apiConfig={apiConfig}
        />
      )}
      {isViewModalVisible && (
    <ViewModal
        record={selectedRecord}
        onClose={() => setIsViewModalVisible(false)}
        onEdit={(record) => {
        setIsViewModalVisible(false);
        handleEdit(record);
        }}
        apiConfig={apiConfig}
        editable={apiConfig.allowEdit !== false && rolePermissions?.[apiConfig.path]?.Edit === 'true'}
    />
    )}

    </div>
)
  );
};

export default DataList;
